import { z } from 'zod';
import { LedgerMethodSchema } from './payment';
/**
 * Deposit top-up requests (`deposit_topup_requests` collection).
 *
 * A top-up request is NOT a money movement: the client announces «I will pay
 * N CHF by bank / in cash», receives a payment reference and instructions, and
 * pays OUTSIDE the platform. A manager then confirms it — and only that
 * confirmation writes a `deposit` row to the append-only `payments` ledger
 * (`providerRef: topup:<requestId>`, `method`, `reference`, `receiptRef`).
 * That is why the request lives in its own collection and never in `payments`.
 *
 * The client chooses only `bank` or `cash` ({@link TopUpRequestMethodSchema});
 * the manager records what actually happened ({@link LedgerMethodSchema}, which
 * adds `card` for an office terminal).
 */
export { LedgerMethodSchema };
/** What a client may choose when requesting a top-up. */
export const TopUpRequestMethodSchema = z.enum(['bank', 'cash']);
/** `open` → exactly one of `confirmed` (manager), `rejected` (manager), `cancelled` (client). */
export const TopUpRequestStatusSchema = z.enum(['open', 'confirmed', 'rejected', 'cancelled']);
/** `DEP-<2 Latin initials>-<MMDD, Europe/Zurich>`, collision suffix `-2`…`-99`. */
export const TOPUP_REFERENCE_PATTERN = /^DEP-[A-Z]{2}-\d{4}(-\d{1,2})?$/;
/** Highest collision suffix {@link TOPUP_REFERENCE_PATTERN} admits. */
export const TOPUP_REFERENCE_MAX_ATTEMPT = 99;
/** Client body of `POST /api/users/me/deposit/topup-requests`. */
export const TopUpRequestCreateSchema = z
    .object({
    amountChf: z.number().int().positive(),
    method: TopUpRequestMethodSchema,
})
    .strict();
/** One top-up request as the API returns it (client and admin views). */
export const TopUpRequestSchema = z.object({
    id: z.string(),
    userId: z.string(),
    reference: z.string().regex(TOPUP_REFERENCE_PATTERN),
    amountChf: z.number().int().positive(),
    currency: z.literal('CHF'),
    method: TopUpRequestMethodSchema,
    status: TopUpRequestStatusSchema,
    createdAt: z.coerce.date(),
    closedAt: z.coerce.date().nullable(),
    /** Admin userId who confirmed/rejected it; the client's own id when cancelled. */
    closedBy: z.string().nullable(),
    /** Amount actually credited — may differ from the announced one (e.g. bank fees). */
    confirmedAmountChf: z.number().nullable(),
    /** How the money actually arrived (manager's record); null until confirmed. */
    ledgerMethod: LedgerMethodSchema.nullable(),
    receiptRef: z.string().nullable(),
    rejectReason: z.string().nullable(),
});
/**
 * Admin body of `POST …/topup-requests/:id/confirm`. `amountChf` defaults to the
 * requested amount. A cash confirmation must carry the cash-desk receipt number.
 */
export const TopUpConfirmSchema = z
    .object({
    amountChf: z.number().positive().optional(),
    method: LedgerMethodSchema,
    receiptRef: z.string().trim().min(1).max(64).optional(),
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.method === 'cash' && !body.receiptRef) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['receiptRef'],
            message: 'required for cash',
        });
    }
});
/** Admin body of `POST …/topup-requests/:id/reject`. */
export const TopUpRejectSchema = z
    .object({
    reason: z.string().trim().min(3).max(500),
})
    .strict();
/** Bank transfer requisites (from `DEPOSIT_BANK_*` env). */
export const BankRequisitesSchema = z.object({
    iban: z.string(),
    beneficiary: z.string(),
    bankName: z.string().nullable(),
    bic: z.string().nullable(),
});
/** Cash office details (from `DEPOSIT_CASH_*` env). */
export const CashOfficeSchema = z.object({
    address: z.string(),
    hours: z.string().nullable(),
    maxChf: z.number().positive(),
});
/** What the client is shown after creating a request: where and how to pay. */
export const TopUpInstructionsSchema = z.discriminatedUnion('method', [
    z.object({
        method: z.literal('bank'),
        reference: z.string(),
        amountChf: z.number(),
        requisites: BankRequisitesSchema,
    }),
    z.object({
        method: z.literal('cash'),
        reference: z.string(),
        amountChf: z.number(),
        office: CashOfficeSchema,
    }),
]);
/** i18n key per ledger method (`packages/i18n/src/{uk,en}/finances.json`, created by the auction wiring). */
export const LEDGER_METHOD_I18N_KEY = {
    bank: 'finances.method.bank',
    card: 'finances.method.card',
    cash: 'finances.method.cash',
};
/**
 * First Latin letter of each Ukrainian Cyrillic letter's KMU-2010
 * transliteration (Resolution of the Cabinet of Ministers of Ukraine No. 55,
 * 2010), as used for an initial: Ж→Z(h), Х→K(h), Ц→T(s), Ч→C(h), Ш→S(h),
 * Щ→S(hch), Г→H, Ґ→G.
 *
 * Є, Ї, Й, Ю, Я map to `I` — the plan's rule (their KMU-2010 non-initial
 * forms `ie/i/i/iu/ia`); strict KMU-2010 would write them word-initially as
 * `Ye/Yi/Y/Yu/Ya`, i.e. `Y`. The owner confirms or overrides this under
 * decision (m) before the reference format ships.
 */
const CYRILLIC_INITIAL = {
    А: 'A',
    Б: 'B',
    В: 'V',
    Г: 'H',
    Ґ: 'G',
    Д: 'D',
    Е: 'E',
    Є: 'I',
    Ж: 'Z',
    З: 'Z',
    И: 'Y',
    І: 'I',
    Ї: 'I',
    Й: 'I',
    К: 'K',
    Л: 'L',
    М: 'M',
    Н: 'N',
    О: 'O',
    П: 'P',
    Р: 'R',
    С: 'S',
    Т: 'T',
    У: 'U',
    Ф: 'F',
    Х: 'K',
    Ц: 'T',
    Ч: 'C',
    Ш: 'S',
    Щ: 'S',
    Ю: 'I',
    Я: 'I',
};
/** Fallback letter for anything that is neither A–Z nor mapped Cyrillic. */
const UNKNOWN_INITIAL = 'X';
/** One uppercase A–Z letter for the first letter of `word`, or `X`. */
function initialLetterOf(word) {
    const first = word.match(/\p{L}/u)?.[0];
    if (first === undefined)
        return UNKNOWN_INITIAL;
    const upper = first.toUpperCase();
    const cyrillic = CYRILLIC_INITIAL[upper];
    if (cyrillic !== undefined)
        return cyrillic;
    // Strip diacritics: É → E, Ü → U; ß uppercases to SS → S. Letters that do
    // not decompose (Ł, Ø) fall to X.
    const base = upper.normalize('NFD').replace(/\p{M}/gu, '').charAt(0);
    return /^[A-Z]$/.test(base) ? base : UNKNOWN_INITIAL;
}
function wordsOf(value) {
    return (value ?? '').split(/\s+/).filter((word) => /\p{L}/u.test(word));
}
/**
 * Two uppercase Latin initials for a top-up reference. Source, in order:
 * `firstName` + `lastName` when both are set; else the first two words of
 * `name`; else whatever single name part exists. Cyrillic is transliterated
 * (see {@link CYRILLIC_INITIAL}); any other non-A–Z letter becomes `X`; a
 * missing second initial is padded with `X`; no name at all → `XX`.
 * Always matches `/^[A-Z]{2}$/`.
 */
export function initialsOf(user) {
    const first = wordsOf(user.firstName)[0];
    const last = wordsOf(user.lastName)[0];
    let parts;
    if (first !== undefined && last !== undefined) {
        parts = [first, last];
    }
    else {
        const fromName = wordsOf(user.name);
        parts =
            fromName.length > 0 ? fromName : [first, last].filter((p) => p !== undefined);
    }
    const letters = parts.slice(0, 2).map(initialLetterOf);
    while (letters.length < 2)
        letters.push(UNKNOWN_INITIAL);
    return letters.join('');
}
const ZURICH_MONTH_DAY = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Zurich',
    month: '2-digit',
    day: '2-digit',
});
/** `MMDD` of `at` on the Europe/Zurich calendar. */
function zurichMonthDay(at) {
    const parts = ZURICH_MONTH_DAY.formatToParts(at);
    const month = parts.find((p) => p.type === 'month')?.value;
    const day = parts.find((p) => p.type === 'day')?.value;
    if (month === undefined || day === undefined) {
        throw new RangeError(`cannot format ${at.toISOString()} in Europe/Zurich`);
    }
    return `${month}${day}`;
}
/**
 * Payment reference for a top-up request: `DEP-AT-0927` on the first attempt,
 * `DEP-AT-0927-<n>` on attempt `n > 1` (the caller retries on a unique-index
 * collision; the index is the race arbiter). The date is the Europe/Zurich
 * calendar day of `at`. Throws a RangeError for initials that are not two
 * uppercase Latin letters, an invalid date, or an attempt outside
 * 1..{@link TOPUP_REFERENCE_MAX_ATTEMPT}.
 */
export function topUpReference(initials, at, attempt) {
    if (!/^[A-Z]{2}$/.test(initials)) {
        throw new RangeError(`initials must be two uppercase Latin letters, got "${initials}"`);
    }
    if (Number.isNaN(at.getTime())) {
        throw new RangeError('at must be a valid date');
    }
    if (!Number.isInteger(attempt) || attempt < 1 || attempt > TOPUP_REFERENCE_MAX_ATTEMPT) {
        throw new RangeError(`attempt must be an integer in 1..${TOPUP_REFERENCE_MAX_ATTEMPT}, got ${attempt}`);
    }
    const base = `DEP-${initials}-${zurichMonthDay(at)}`;
    return attempt === 1 ? base : `${base}-${attempt}`;
}
//# sourceMappingURL=deposit-topup.js.map